import React from "react";
import { Link } from "gatsby";
import { LayoutInsight as Layout } from "~/templates/LayoutInsight";
import type { InsightStructuredDocument } from "~/molecules/InsightRichTextBody";
import { buildInsightTocFromRichText, renderInsightRichText } from "~/molecules/InsightRichTextBody";
import { colors } from "~/utils/colors";
import { breakPoints } from "~/utils/variables";

const introductionContent: InsightStructuredDocument = {
  raw: JSON.stringify({
    nodeType: "document",
    data: {},
    content: [
      {
        nodeType: "paragraph",
        data: {},
        content: [
          {
            nodeType: "text",
            value:
              "これは見た目確認用のサンプルページです。実際のInsight記事に、CTA、Refcome内URLカード、コールアウト、フォーム、埋め込みを差し込むとどんな雰囲気になるかをブラウザで確認できます。",
            marks: [],
            data: {},
          },
        ],
      },
    ],
  }),
  references: [],
  plainText:
    "これは見た目確認用のサンプルページです。実際のInsight記事に、CTA、Refcome内URLカード、コールアウト、フォーム、埋め込みを差し込むとどんな雰囲気になるかをブラウザで確認できます。",
};

const createRichTextDocument = (content: any[], plainText: string): InsightStructuredDocument => ({
  raw: JSON.stringify({
    nodeType: "document",
    data: {},
    content,
  }),
  references: [],
  plainText,
});

const bodyContent: InsightStructuredDocument = {
  raw: JSON.stringify({
    nodeType: "document",
    data: {},
    content: [
      {
        nodeType: "heading-2",
        data: {},
        content: [{ nodeType: "text", value: "CTAの見え方", marks: [], data: {} }],
      },
      {
        nodeType: "paragraph",
        data: {},
        content: [
          {
            nodeType: "text",
            value: "本文の流れの中で、資料請求や問い合わせにつなげたい場所にCTAを差し込めます。",
            marks: [],
            data: {},
          },
        ],
      },
      {
        nodeType: "embedded-entry-block",
        data: {
          target: {
            sys: {
              type: "Link",
              linkType: "Entry",
              id: "demo-cta-block",
            },
          },
        },
        content: [],
      },
      {
        nodeType: "heading-2",
        data: {},
        content: [{ nodeType: "text", value: "Refcome内URLカードの見え方", marks: [], data: {} }],
      },
      {
        nodeType: "paragraph",
        data: {},
        content: [
          {
            nodeType: "text",
            value: "Insightや資料などのRefcome内URLを本文に1行だけ貼ると、Notionのようなカード表示に自動で変わります。",
            marks: [],
            data: {},
          },
        ],
      },
      {
        nodeType: "paragraph",
        data: {},
        content: [{ nodeType: "text", value: "https://jp.refcome.com/insight/5028", marks: [], data: {} }],
      },
      {
        nodeType: "heading-2",
        data: {},
        content: [{ nodeType: "text", value: "コールアウトの見え方", marks: [], data: {} }],
      },
      {
        nodeType: "paragraph",
        data: {},
        content: [
          {
            nodeType: "text",
            value: "補足、ポイント、注意点のような短いコラムを、本文の途中に箱型で差し込めます。",
            marks: [],
            data: {},
          },
        ],
      },
      {
        nodeType: "embedded-entry-block",
        data: {
          target: {
            sys: {
              type: "Link",
              linkType: "Entry",
              id: "demo-callout-note",
            },
          },
        },
        content: [],
      },
      {
        nodeType: "embedded-entry-block",
        data: {
          target: {
            sys: {
              type: "Link",
              linkType: "Entry",
              id: "demo-callout-tip",
            },
          },
        },
        content: [],
      },
      {
        nodeType: "embedded-entry-block",
        data: {
          target: {
            sys: {
              type: "Link",
              linkType: "Entry",
              id: "demo-callout-warning",
            },
          },
        },
        content: [],
      },
      {
        nodeType: "embedded-entry-block",
        data: {
          target: {
            sys: {
              type: "Link",
              linkType: "Entry",
              id: "demo-callout-no-title",
            },
          },
        },
        content: [],
      },
      {
        nodeType: "heading-2",
        data: {},
        content: [{ nodeType: "text", value: "フォーム埋め込みの見え方", marks: [], data: {} }],
      },
      {
        nodeType: "heading-3",
        data: {},
        content: [{ nodeType: "text", value: "記事の途中で申込みにつなげる", marks: [], data: {} }],
      },
      {
        nodeType: "paragraph",
        data: {},
        content: [
          {
            nodeType: "text",
            value: "HubSpotの共有URLを本文にそのまま貼ると、自動でフォームとして表示されます。下の例では実際のHubSpotフォーム枠を表示します。",
            marks: [],
            data: {},
          },
        ],
      },
      {
        nodeType: "paragraph",
        data: {},
        content: [
          {
            nodeType: "text",
            value: "https://share-na2.hsforms.com/2k8Np-AI8QSqPhdTVYEuR_g4026jo",
            marks: [],
            data: {},
          },
        ],
      },
      {
        nodeType: "heading-2",
        data: {},
        content: [{ nodeType: "text", value: "埋め込みの見え方", marks: [], data: {} }],
      },
      {
        nodeType: "paragraph",
        data: {},
        content: [
          {
            nodeType: "text",
            value: "YouTubeの通常URLを本文にそのまま貼ると、自動で動画埋め込みに変わります。下の例では watch URL をそのまま置いています。",
            marks: [],
            data: {},
          },
        ],
      },
      {
        nodeType: "paragraph",
        data: {},
        content: [
          {
            nodeType: "text",
            value: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
            marks: [],
            data: {},
          },
        ],
      },
    ],
  }),
  references: [
    {
      __typename: "ContentfulInsightCtaBlock",
      contentful_id: "demo-cta-block",
      title: "採用広報の進め方をまとめた資料を配布中",
      description: "記事を読んで興味が出た方向けに、すぐ次の行動につながるCTAの見本です。",
      buttonLabel: "資料をダウンロード",
      buttonUrl: "/request",
      note: "フォーム入力は1分ほどで完了します。",
    },
    {
      __typename: "ContentfulInsightCallout",
      contentful_id: "demo-callout-note",
      label: "補足",
      variant: "note",
      title: "用語の前提をそろえる補足",
      bodyRichText: createRichTextDocument(
        [
          {
            nodeType: "paragraph",
            data: {},
            content: [
              { nodeType: "text", value: "本文では ", marks: [], data: {} },
              { nodeType: "text", value: "リファラル採用", marks: [{ type: "bold" }], data: {} },
              { nodeType: "text", value: " を社員紹介による採用全般の意味で使っています。", marks: [], data: {} },
            ],
          },
          {
            nodeType: "paragraph",
            data: {},
            content: [
              { nodeType: "text", value: "先に全体像を確認したい場合は ", marks: [], data: {} },
              {
                nodeType: "hyperlink",
                data: { uri: "/request" },
                content: [{ nodeType: "text", value: "資料ページ", marks: [], data: {} }],
              },
              { nodeType: "text", value: " も参照できます。", marks: [], data: {} },
            ],
          },
        ],
        "本文ではリファラル採用を社員紹介による採用全般の意味で使っています。先に全体像を確認したい場合は資料ページも参照できます。"
      ),
    },
    {
      __typename: "ContentfulInsightCallout",
      contentful_id: "demo-callout-tip",
      label: "ポイント",
      variant: "tip",
      title: "先に押さえると読みやすいポイント",
      bodyRichText: createRichTextDocument(
        [
          {
            nodeType: "unordered-list",
            data: {},
            content: [
              {
                nodeType: "list-item",
                data: {},
                content: [
                  {
                    nodeType: "paragraph",
                    data: {},
                    content: [{ nodeType: "text", value: "制度設計より先に紹介しやすい求人を決める", marks: [], data: {} }],
                  },
                ],
              },
              {
                nodeType: "list-item",
                data: {},
                content: [
                  {
                    nodeType: "paragraph",
                    data: {},
                    content: [{ nodeType: "text", value: "紹介後の選考フローを短く保つ", marks: [], data: {} }],
                  },
                ],
              },
            ],
          },
        ],
        "制度設計より先に紹介しやすい求人を決める 紹介後の選考フローを短く保つ"
      ),
    },
    {
      __typename: "ContentfulInsightCallout",
      contentful_id: "demo-callout-warning",
      label: "注意",
      variant: "warning",
      title: "運用時の注意点",
      bodyRichText: createRichTextDocument(
        [
          {
            nodeType: "paragraph",
            data: {},
            content: [
              {
                nodeType: "text",
                value: "紹介数だけを追うと、ミスマッチ応募が増えることがあります。応募数だけでなく面接通過率も一緒に見て判断するのがおすすめです。",
                marks: [],
                data: {},
              },
            ],
          },
        ],
        "紹介数だけを追うと、ミスマッチ応募が増えることがあります。応募数だけでなく面接通過率も一緒に見て判断するのがおすすめです。"
      ),
    },
    {
      __typename: "ContentfulInsightCallout",
      contentful_id: "demo-callout-no-title",
      variant: "note",
      bodyRichText: createRichTextDocument(
        [
          {
            nodeType: "paragraph",
            data: {},
            content: [
              {
                nodeType: "text",
                value: "title を入れない場合は、このように本文だけのシンプルなコールアウトとして表示されます。",
                marks: [],
                data: {},
              },
            ],
          },
        ],
        "title を入れない場合は、このように本文だけのシンプルなコールアウトとして表示されます。"
      ),
    },
  ],
  plainText:
    "CTAの見え方 Refcome内URLカードの見え方 コールアウトの見え方 フォーム埋め込みの見え方 埋め込みの見え方",
};

const InsightPreviewPage: React.FC = () => {
  const tocItems = buildInsightTocFromRichText(bodyContent);

  return (
    <>
      <style jsx>{`
        .wrapper {
          margin: 0 auto;
          max-width: 800px;
          padding: 0 20px 80px;
        }
        .hero {
          margin: 0 auto 40px;
          max-width: 800px;
          padding: 40px 20px 0;
        }
        .eyebrow {
          color: ${colors.brand};
          display: inline-block;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.08em;
          margin-bottom: 12px;
          text-transform: uppercase;
        }
        .title {
          color: ${colors.defaultText};
          font-size: 40px;
          font-weight: 500;
          margin: 0 0 16px;
        }
        .lead {
          color: ${colors.defaultText};
          font-size: 18px;
          line-height: 1.9;
          margin: 0;
        }
        .tocWrapper {
          box-sizing: border-box;
          margin: 40px auto;
          max-width: 100%;
          padding: 0;
          width: 100%;
        }
        .toc {
          background: ${colors.lightGray};
          border: 1px solid ${colors.gray3};
          border-radius: 12px;
          box-shadow: none;
          box-sizing: border-box;
          overflow: hidden;
          padding: 20px 22px 22px;
          width: 100%;
        }
        .tocTitle {
          border-bottom: 1px solid ${colors.gray3};
          color: ${colors.defaultText};
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.03em;
          line-height: 1.45;
          margin: 0 0 14px;
          padding: 0 0 12px;
        }
        .tocList {
          display: flex;
          flex-direction: column;
          gap: 8px;
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .tocItem {
          margin: 0;
          padding: 0;
        }
        .tocItem :global(a) {
          color: ${colors.defaultText};
          font-size: 15px;
          line-height: 1.55;
          text-decoration: none;
          transition: color 0.15s ease-out, opacity 0.15s ease-out;
        }
        .tocItem :global(a:hover) {
          color: ${colors.defaultText};
          opacity: 1;
          text-decoration: underline;
          text-decoration-thickness: 1px;
          text-underline-offset: 3px;
        }
        .previewBody {
          margin-top: 24px;
        }
        .backLink {
          color: ${colors.orange};
          display: inline-block;
          margin-top: 40px;
          text-decoration: none;
        }
        .backLink:hover {
          text-decoration: underline;
        }
        @media (${breakPoints.minPc}) {
          .tocWrapper {
            max-width: 600px;
          }
        }
        @media (${breakPoints.sp}) {
          .hero {
            padding-top: 24px;
          }
          .title {
            font-size: 30px;
          }
          .lead {
            font-size: 15px;
          }
          .tocWrapper {
            margin: 28px auto;
          }
          .toc {
            padding: 18px 16px 20px;
          }
          .tocTitle {
            font-size: 14px;
            letter-spacing: 0.02em;
            margin-bottom: 12px;
            padding-bottom: 10px;
          }
          .tocItem :global(a) {
            font-size: 14px;
          }
        }
      `}</style>
      <Layout title="Insightデザイン確認" description="Insight記事の新しい差し込み部品の見た目確認ページです。">
        <div className="hero">
          <span className="eyebrow">Preview</span>
          <h1 className="title">Insight記事の見た目サンプル</h1>
          <p className="lead">
            `InsightRichTextBody.tsx` のコードをそのまま読む代わりに、実際の画面として見られる確認ページです。
          </p>
          <div>{renderInsightRichText(introductionContent)}</div>
        </div>
        <div className="wrapper">
          {tocItems.length > 0 && (
            <div className="tocWrapper">
              <nav className="toc" aria-label="この記事の目次">
                <p className="tocTitle">目次</p>
                <ol className="tocList">
                  {tocItems.map((item) => (
                    <li key={item.headingText} className="tocItem">
                      <Link to={`#${item.headingText}`}>{item.headingText}</Link>
                    </li>
                  ))}
                </ol>
              </nav>
            </div>
          )}
          <div className="previewBody">{renderInsightRichText(bodyContent)}</div>
          <Link to="/insight" className="backLink">
            Insight一覧へ戻る
          </Link>
        </div>
      </Layout>
    </>
  );
};

export default InsightPreviewPage;
